if (!customElements.get('agency-blog-deck')) {
  customElements.define('agency-blog-deck', class extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.slides = [...this.querySelectorAll('[data-blog-slide]')];
      if (!this.slides.length) return;

      this.previous = this.querySelector('[data-blog-prev]');
      this.next = this.querySelector('[data-blog-next]');
      this.dots = [...this.querySelectorAll('[data-blog-dot]')];
      this.status = this.querySelector('[data-blog-status]');
      this.index = 0;
      this.requestedIndex = 0;
      this.motionAnimations = [];
      this.motionVersion = (this.motionVersion || 0) + 1;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = null;
      this.suppressClickUntil = 0;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.motionQuery.addEventListener('change', () => {
        if (this.motionQuery.matches) this.settleMotion();
      }, { signal });

      this.previous?.addEventListener('click', () => this.move(-1), { signal });
      this.next?.addEventListener('click', () => this.move(1), { signal });
      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.blogDot);
          if (Number.isInteger(index) && index >= 0 && index < this.slides.length) this.show(index);
        }, { signal });
      });

      this.addEventListener('keydown', event => {
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (this.isEditing(event.target)) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        this.move((event.key === 'ArrowRight' ? 1 : -1) * direction);
      }, { signal });

      this.addEventListener('pointerdown', event => {
        this.suppressClickUntil = 0;
        if (!event.isPrimary || event.button !== 0 || this.slides.length < 2) return;
        if (this.isEditing(event.target) || event.target.closest('button')) return;
        if (!event.target.closest('[data-blog-slide][data-active]')) return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, vertical: false };
      }, { passive: true, signal });

      this.addEventListener('pointermove', event => {
        if (!this.pointer || event.pointerId !== this.pointer.id) return;
        const x = Math.abs(event.clientX - this.pointer.x);
        const y = Math.abs(event.clientY - this.pointer.y);
        if (y > 12 && y > x) this.pointer.vertical = true;
      }, { passive: true, signal });

      document.addEventListener('pointerup', event => {
        if (!this.pointer || event.pointerId !== this.pointer.id) return;
        const pointer = this.pointer;
        this.pointer = null;
        const x = event.clientX - pointer.x;
        const y = event.clientY - pointer.y;
        if (pointer.vertical || Math.abs(x) < 40 || Math.abs(x) < Math.abs(y) * 1.25) return;
        this.suppressClickUntil = performance.now() + 500;
        const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        this.move((x < 0 ? 1 : -1) * direction);
      }, { passive: true, signal });

      document.addEventListener('pointercancel', event => {
        if (event.pointerId === this.pointer?.id) this.pointer = null;
      }, { passive: true, signal });

      this.addEventListener('dragstart', event => {
        if (this.pointer) event.preventDefault();
      }, { signal });

      this.addEventListener('click', event => {
        if (event.detail === 0 || performance.now() > this.suppressClickUntil) return;
        this.suppressClickUntil = 0;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { capture: true, signal });

      this.render(false);
      this.setupGlare();
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      this.clearMotion();
      this.restoreFocusAfterMotion = false;
      this.resetGlare?.({ immediate: true });
      this.abortController?.abort();
      this.abortController = null;
      this.pointer = null;
      this.suppressClickUntil = 0;
    }

    isEditing(target) {
      return target.isContentEditable || target.closest('input, textarea, select, option, [role="textbox"], [role="combobox"], [role="slider"], [role="spinbutton"]');
    }

    move(direction) {
      this.show((this.requestedIndex + direction + this.slides.length) % this.slides.length, direction);
    }

    show(index, direction) {
      if (index === this.requestedIndex) return;
      const distance = (index - this.index + this.slides.length) % this.slides.length;
      this.pendingDirection = direction || (distance <= this.slides.length / 2 ? 1 : -1);
      this.requestedIndex = index;
      this.restoreFocusAfterMotion ||= this.slides[this.index].contains(document.activeElement);
      this.resetGlare?.({ immediate: true });
      if (this.motionQuery.matches || !this.slides[0].animate) {
        this.settleMotion();
      } else if (!this.hasAttribute('data-transitioning')) {
        this.animateTo(index, this.pendingDirection);
      }
    }

    clearMotion() {
      this.motionVersion += 1;
      this.motionAnimations?.forEach(animation => animation.cancel());
      this.motionAnimations = [];
      this.removeAttribute('data-transitioning');
      this.slides?.forEach(slide => {
        slide.removeAttribute('data-entering');
        slide.removeAttribute('data-leaving');
      });
    }

    settleMotion() {
      this.clearMotion();
      this.index = this.requestedIndex;
      this.render(true);
      this.restoreMotionFocus();
    }

    restoreMotionFocus() {
      if (!this.restoreFocusAfterMotion) return;
      this.restoreFocusAfterMotion = false;
      const target = this.slides[this.index].querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
        || this.dots.find(dot => Number(dot.dataset.blogDot) === this.index);
      target?.focus({ preventScroll: true });
    }

    animateTo(index, direction) {
      const snapshot = slide => {
        const style = getComputedStyle(slide);
        return { transform: style.transform, opacity: style.opacity, zIndex: style.zIndex, visible: style.visibility !== 'hidden' };
      };
      const before = this.slides.map(snapshot);
      const oldIndex = this.index;
      const rear = this.slides.findIndex(slide => slide.dataset.position === (direction > 0 ? '1' : '2'));
      const enteringTransform = before[rear]?.transform || before[index].transform;
      this.setAttribute('data-transitioning', '');
      this.slides[oldIndex].setAttribute('data-leaving', '');
      this.slides[index].setAttribute('data-entering', '');
      this.index = index;
      this.render(true);
      const after = this.slides.map(snapshot);
      const version = ++this.motionVersion;
      const options = { duration: 520, easing: 'linear', fill: 'both' };
      this.slides.forEach((slide, slideIndex) => {
        const start = before[slideIndex];
        const end = after[slideIndex];
        const visible = { visibility: 'visible' };
        let frames;
        if (slideIndex === index) {
          frames = [
            { ...visible, transform: start.visible ? start.transform : enteringTransform, opacity: start.visible ? start.opacity : 0, zIndex: 4, offset: 0, easing: 'ease-out' },
            { ...visible, transform: 'translate3d(0, -12px, 0)', opacity: 1, zIndex: 4, offset: .45, easing: 'ease-out' },
            { ...visible, transform: end.transform, opacity: 1, zIndex: 4, offset: 1 }
          ];
        } else if (slideIndex === oldIndex) {
          frames = [
            { ...visible, transform: start.transform, opacity: 1, zIndex: 5, offset: 0, easing: 'cubic-bezier(.4, 0, .2, 1)' },
            { ...visible, transform: `translate3d(${-direction * 24}px, -14px, 0) rotate(${-direction * 3}deg)`, opacity: 0, zIndex: 5, offset: .4 },
            { ...visible, transform: end.transform, opacity: 0, zIndex: end.zIndex, offset: .41, easing: 'ease-out' },
            { ...visible, transform: end.transform, opacity: end.visible ? end.opacity : 0, zIndex: end.zIndex, offset: 1 }
          ];
        } else if (start.visible || end.visible) {
          frames = [
            { ...visible, transform: start.visible ? start.transform : end.transform, opacity: start.visible ? start.opacity : 0, zIndex: end.zIndex, easing: 'cubic-bezier(.4, 0, .2, 1)' },
            { ...visible, transform: end.transform, opacity: end.visible ? end.opacity : 0, zIndex: end.zIndex }
          ];
        }
        if (frames) this.motionAnimations.push(slide.animate(frames, options));
      });
      const content = this.slides[index].querySelector('.card__content');
      if (content) this.motionAnimations.push(content.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 360, delay: 80, easing: 'ease-out', fill: 'both' }));
      Promise.allSettled(this.motionAnimations.map(animation => animation.finished)).then(() => {
        if (version !== this.motionVersion) return;
        this.clearMotion();
        if (this.requestedIndex !== this.index) this.animateTo(this.requestedIndex, this.pendingDirection);
        else this.restoreMotionFocus();
      });
    }

    setupGlare() {
      const { signal } = this.abortController;
      const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const entries = [];
      const timers = new Map();
      let frame = null;
      let pending = null;

      const clearTrackingTimer = card => {
        const timer = timers.get(card);
        if (timer !== undefined) window.clearTimeout(timer);
        timers.delete(card);
      };

      const queueGlare = (card, x, y) => {
        pending = { card, x, y };
        if (frame !== null) return;
        frame = window.requestAnimationFrame(() => {
          const position = pending;
          frame = null;
          pending = null;
          if (!position) return;

          const { card, x, y } = position;
          card.style.setProperty('--agency-glare-x', `${x}%`);
          card.style.setProperty('--agency-glare-y', `${y}%`);
          card.style.setProperty('--agency-glare-bg-x', `${37.5 + x / 4}%`);
          card.style.setProperty('--agency-glare-bg-y', `${33.333333 + y / 3}%`);
        });
      };

      this.slides.forEach(slide => {
        const card = slide.querySelector('[data-blog-glare-card]');
        if (!card) return;
        let cardRect = null;

        const reset = ({ immediate = false } = {}) => {
          cardRect = null;
          clearTrackingTimer(card);
          if (pending?.card === card) {
            pending = null;
            if (frame !== null) window.cancelAnimationFrame(frame);
            frame = null;
          }
          slide.classList.remove('is-glare-active', 'is-glare-tracking');
          if (immediate) card.style.setProperty('--agency-glare-duration', '0ms');
          else card.style.removeProperty('--agency-glare-duration');
          card.style.setProperty('--agency-glare-x', '50%');
          card.style.setProperty('--agency-glare-y', '50%');
          card.style.setProperty('--agency-glare-bg-x', '50%');
          card.style.setProperty('--agency-glare-bg-y', '50%');
        };
        entries.push(reset);

        const canTrack = event => event.pointerType === 'mouse'
          && finePointerQuery.matches
          && !reducedMotionQuery.matches
          && !this.hasAttribute('data-transitioning')
          && slide.hasAttribute('data-active');

        slide.addEventListener('pointerenter', event => {
          if (!canTrack(event)) return;
          cardRect = card.getBoundingClientRect();
          if (!cardRect.width || !cardRect.height) return;
          clearTrackingTimer(card);
          slide.classList.add('is-glare-active');
          slide.classList.remove('is-glare-tracking');
          card.style.removeProperty('--agency-glare-duration');
          timers.set(card, window.setTimeout(() => {
            timers.delete(card);
            if (slide.classList.contains('is-glare-active')) slide.classList.add('is-glare-tracking');
          }, 300));
        }, { signal });

        slide.addEventListener('pointermove', event => {
          if (!canTrack(event) || !cardRect?.width || !cardRect?.height) return;
          const x = Math.max(0, Math.min(100, ((event.clientX - cardRect.left) / cardRect.width) * 100));
          const y = Math.max(0, Math.min(100, ((event.clientY - cardRect.top) / cardRect.height) * 100));
          slide.classList.add('is-glare-active');
          queueGlare(card, x, y);
        }, { passive: true, signal });

        slide.addEventListener('pointerleave', () => reset(), { signal });
        slide.addEventListener('pointercancel', () => reset(), { signal });
      });

      this.resetGlare = (options = {}) => {
        entries.forEach(reset => reset(options));
        if (frame !== null) window.cancelAnimationFrame(frame);
        frame = null;
        pending = null;
        timers.forEach(timer => window.clearTimeout(timer));
        timers.clear();
      };

      const resetOnPreferenceChange = () => {
        if (!finePointerQuery.matches || reducedMotionQuery.matches) this.resetGlare({ immediate: true });
      };
      finePointerQuery.addEventListener('change', resetOnPreferenceChange, { signal });
      reducedMotionQuery.addEventListener('change', resetOnPreferenceChange, { signal });
    }

    render(announce) {
      const total = this.slides.length;
      this.slides.forEach((slide, index) => {
        const position = (index - this.index + total) % total;
        const active = position === 0;
        slide.dataset.position = position < 3 ? String(position) : 'hidden';
        slide.toggleAttribute('data-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach(dot => dot.setAttribute('aria-current', String(Number(dot.dataset.blogDot) === this.index)));
      if (this.previous) this.previous.disabled = total < 2;
      if (this.next) this.next.disabled = total < 2;
      if (announce && this.status) {
        this.status.textContent = (this.dataset.statusTemplate || 'Article [current] of [total]')
          .replaceAll('[current]', String(this.index + 1))
          .replaceAll('[total]', String(total));
      }
    }
  });
}
